/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1752019200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jul 09 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "linear",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported Syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Linear",
          "depth": 0,
          "id": "connect-to-linear"
        }, {
          "value": "Sync Configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing Issues",
          "depth": 1,
          "id": "syncing-issues"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ol: "ol",
      li: "li",
      h3: "h3",
      h4: "h4",
      ul: "ul"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Linear is a popular issue-tracking tool designed for high-performance teams. It helps streamline workflows for software projects, sprints, and tasks."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's Linear destination allows you to sync data from your warehouse to create and update issues in Linear. This can be useful for automating bug reports, feature requests, and other tasks based on events or data in your own systems."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported Syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Issues"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Linear issues."
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert, Delete"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-linear",
        children: "Connect to Linear"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Linear"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Linear"
        }), " by providing your API Key."]
      }), "\n", _jsx(_components.p, {
        children: "To get your API Key:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to your Linear account."
        }), "\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.strong, {
            children: "Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Security & access"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Personal API keys"
          }), ", create a new key."]
        }), "\n", _jsx(_components.li, {
          children: "Copy the key and paste it into the Hightouch configuration."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync Configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your ", _jsx(_components.strong, {
          children: "Linear"
        }), " destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Linear destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-issues",
        children: "Syncing Issues"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to create, update, or delete issues in Linear based on your model data."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record Matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to issues in Linear using the ", _jsx(_components.strong, {
          children: "Issue ID"
        }), ". ", _jsx(_components.strong, {
          children: "Issue ID"
        }), " must be a UUIDv4."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Linear automatically generates an identifier for every new\nrecord synced if one is missing."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field Mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can map data from any of your model columns to Linear issue fields. Hightouch only requires you to map the ", _jsx(_components.strong, {
          children: "Title"
        }), " field."]
      }), "\n", _jsx(_components.p, {
        children: "These are the additional fields you can map:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Description"
        }), "\n", _jsx(_components.li, {
          children: "Priority"
        }), "\n", _jsx(_components.li, {
          children: "Assignee ID"
        }), "\n", _jsx(_components.li, {
          children: "Project ID"
        }), "\n", _jsx(_components.li, {
          children: "State ID"
        }), "\n", _jsx(_components.li, {
          children: "Label IDs"
        }), "\n", _jsx(_components.li, {
          children: "Due Date"
        }), "\n", _jsx(_components.li, {
          children: "Estimate"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete Behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the issue in Linear with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced issues from Linear"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
